import React, {useEffect, useState} from 'react';
import {useAppointmentStore} from "../stores/useAppointmentStore";
import {useAuthStore} from "../stores/useAuthStore";
import {Header} from "semantic-ui-react";
import {DynamicTable} from "pivot-table";
import {useNavigate} from "react-router-dom";
import {useUiStore} from "../stores/useUiStore";
import {Chceckbox} from "semantic-ui-react";
import CRUModal from "./CRUModal";
import TableLegend from "./TableLegend";

const GeneralDashboard = () => {
    const fetchAppointmentDates = useAppointmentStore(state => state.fetchAppointmentDates);
    const exportEventListPDF = useAppointmentStore(state => state.exportEventListPDF);
    const exportEventListCSV = useAppointmentStore(state => state.exportEventListCSV);
    const exportFamilyListCSV = useAppointmentStore(state => state.exportFamilyListCSV);
    const exportFamilyListDaTCSV = useAppointmentStore(state => state.exportFamilyListDaTCSV);
    const setBackgroundUrl = useUiStore(state => state.setBackgroundUrl);
    const toggleWeekendScheduling = useAuthStore(state => state.toggleWeekendScheduling);

    const appointmentDates = useAppointmentStore(state => state.appointmentDates);
    const noAppointmentDatesMessage= useAppointmentStore(state => state.noAppointmentDatesMessage);
    const ucdId = useAuthStore(state => state.ucdId);
    const churchPassword = useAuthStore(state => state.churchPassword);
    const churchId = useAuthStore(state => state.churchId);
    const churchName = useAuthStore(state => state.churchName);
    const isAdmin = useAuthStore(state => state.isAdmin);
    const weekendSchedulingDisabled = useAuthStore(state => state.weekendSchedulingDisabled);

    const loading = useAuthStore(state => state.loading);

    const navigate = useNavigate();

    const [emailModalState, setEmailModalState] = useState({
        isOpen: false,
        mode: 'create',
        data: null,
        appointmentDate: null
    });

    const [resultsModalState, setResultsModalState] = useState({
        isOpen: false,
        mode: 'view',
        data: null,
        appointmentDate: null
    });

    const sendEmailReminders = useAppointmentStore(state => state.sendEmailReminders);
    const emailStatus = useAppointmentStore(state => state.emailStatus);
    const clearEmailStatus = useAppointmentStore(state => state.clearEmailStatus);

    const emailFields = [
        {
            name: 'subject',
            label: 'Subject',
            type: 'text',
            validation: {
                required: true
            }
        },
        {
            name: 'message',
            label: 'Message',
            type: 'textarea',
            rows: 6,
            validation: {
                required: true
            }
        }
    ];

    const resultsFields = [
        {
            name: 'results',
            label: 'Email Delivery Results',
            type: 'html',
            disabled: true
        }
    ];

    const isWeekend = () => {
        const now = new Date();
        const estOffset = -5; // EST offset from UTC

        // Convert current time to EST
        const estTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000) + (estOffset * 3600000));
        const estDay = estTime.getDay();

        // Check if it's Saturday or Sunday in EST
        return estDay === 0 || estDay === 6;
    };

    useEffect(() => {
        async function fetchAppointmentDatesHandler() {
            if (churchId && ucdId && churchPassword) {
                const params = {
                    church_id: churchId,
                    ucd_church_id: ucdId,
                    password: churchPassword
                }
                await fetchAppointmentDates(params, isAdmin)
            }
        }

        fetchAppointmentDatesHandler()

        setBackgroundUrl('https://schedule.ucdir.com/images/backgrounds/portrait-guide-550.jpg')
    }, []);

    const handleNavigate = (data) => {
        navigate(`/schedule-appointment`, {state: {data: data}});
    }

    const handlePDFExport = async (data) => {
        await exportEventListPDF({
            appointment_date_id: data._id,
            ucd_church_id: ucdId,
            church_admin_login: churchPassword
        });
    }

    const handleCSVExport = async (data) => {
        await exportEventListCSV({
            appointment_date_id: data._id,
            ucd_church_id: ucdId,
            church_admin_login: churchPassword
        });
    }

    const handleMassExport = async () => {
        await exportFamilyListCSV({
            church_id: churchId,
            ucd_church_id: ucdId,
            church_admin_login: churchPassword
        });
    }

    const handleMassExportDaT = async () => {
        await exportFamilyListDaTCSV({
            church_id: churchId,
            ucd_church_id: ucdId,
            church_admin_login: churchPassword
        });
    }

    const handleEmailReminders = (data) => {
        setEmailModalState({
            isOpen: true,
            mode: 'create',
            data: {
                subject: `Photo Session Reminder - ${data.appointmentDate}`,
                message: `This is a reminder about your upcoming photo session on ${data.appointmentDate}. Please arrive 10 minutes before your scheduled time.`,
                appointment_date_id: data._id
            },
            appointmentDate: data.appointmentDate
        });
    };

    const handleEmailSubmit = async (formData) => {
        const payload = {
            appointment_date_id: formData.appointment_date_id,
            ucd_church_id: ucdId,
            church_admin_login: churchPassword,
            subject: formData.subject,
            message: formData.message
        };

        const result = await sendEmailReminders(payload);
        if (result?.delivery_report) {
            setEmailModalState({
                isOpen: false,
                mode: 'create',
                data: null,
                appointmentDate: null
            });

            setResultsModalState({
                isOpen: true,
                mode: 'view',
                data: {
                    results: result.delivery_report
                },
                appointmentDate: emailModalState.appointmentDate
            });
        }
    };

    const handleEmailModalClose = () => {
        setEmailModalState({
            isOpen: false,
            mode: 'create',
            data: null,
            appointmentDate: null
        });
        clearEmailStatus();
    };

    const handleResultsModalClose = () => {
        setResultsModalState({
            isOpen: false,
            mode: 'view',
            data: null,
            appointmentDate: null
        });
    };

    const handleWeekendSchedulingToggle = async () => {
        const params = {
            ucd_church_id: ucdId,
            church_login: churchPassword,
            _id: churchId,
            disable_weekend_scheduling: !weekendSchedulingDisabled
        }
        await toggleWeekendScheduling(isAdmin, params);
    }

    let options;

    if (isAdmin) {
        options = {
            rowSelect: false,
            columnSorting: false,
            searchEnabled: false,
            pagination: false,
            rowsPerPageDisplay: false,
            export: {enabled: false, showExportAll: false, showExportSelected: false},
            selectedRowsTable: {enabled: false},
            rowFunctions: [
                {callback: handleNavigate, color: 'blue', icon: 'clock outline', name: 'View Timeslots'},
                {callback: handlePDFExport, color: 'red', icon: 'file pdf outline'},
                {callback: handleCSVExport, color: 'green', icon: 'file excel outline'},
                {callback: handleEmailReminders, color: 'blue', icon: 'mail outline'}
            ],
            generalButtons: [
                {
                    operationType: 'create',
                    callback: handleMassExportDaT,
                    color: 'green',
                    icon: 'download',
                    title: 'Download a List of Scheduled Families (Sorted By Name)'
                },
                {
                    operationType: 'create',
                    callback: handleMassExport,
                    color: 'green',
                    icon: 'download',
                    title: 'Download a List of Scheduled Families (Sorted by Date and Time)'
                },
            ]
        };
    } else {
        options = {
            rowSelect: false,
            columnSorting: false,
            searchEnabled: false,
            pagination: false,
            rowsPerPageDisplay: true,
            export: {enabled: false, showExportAll: false, showExportSelected: false},
            selectedRowsTable: {enabled: false},
            rowFunctions: [
                {callback: handleNavigate, color: 'blue', icon: 'clock outline', name: 'View Timeslots'}
            ]
        };
    }

    const legendItems = {
        0: { permission: ['allow', 'all'], icon: 'file pdf outline', color: 'red', label: 'Export PDF' },
        1: { permission: ['allow', 'all'], icon: 'file excel outline', color: 'green', label: 'Export CSV' },
        2: { permission: ['allow', 'all'], icon: 'mail outline', color: 'blue', label: 'Send Email Reminders' },
    };

    // Show weekend message if scheduling is disabled, user is not admin, and it's the weekend
    if (weekendSchedulingDisabled && !isAdmin && isWeekend()) {
        return (
            <div className="section-wrapper">
                <p>Online signups are available Monday - Friday.</p>
                <p>On the weekends signup is in person at the church.</p>
            </div>
        );
    }

    return (
        <div className="section-wrapper">
            <p>Welcome to the {churchName} photo schedule. </p>
            {isAdmin && (
                <div style={{marginBottom: 5}}>
                    <Header as="h3" style={{display: 'flex', alignItems: 'center'}}>
                        <span style={{marginRight: '10px'}}>Allow online signups on the weekend: </span>
                        <div className="ui fitted toggle checkbox">
                            <input
                                type="checkbox"
                                checked={!weekendSchedulingDisabled}
                                onClick={() => handleWeekendSchedulingToggle()}
                                style={{marginLeft: '15px'}}
                            />
                            <label></label>
                        </div>
                    </Header>
                    <p style={{marginTop: -10, marginBottom: 15}}>
                        Online schedule access for members is automatically disabled from midnight Friday to midnight Sunday.
                        The button above will enable weekend access if necessary.
                    </p>
                </div>
            )}

            {isAdmin && (
                <>
                    <CRUModal
                        isOpen={emailModalState.isOpen}
                        onClose={handleEmailModalClose}
                        title={`Send Email Reminders - ${emailModalState.appointmentDate}`}
                        data={emailModalState.data}
                        fields={emailFields}
                        onSave={handleEmailSubmit}
                        mode={emailModalState.mode}
                        buttonLabels={{
                            genericCancelLabel: 'Cancel',
                            create: {
                                saveLabel: 'Send Reminders',
                                cancelLabel: 'Cancel'
                            }
                        }}
                    />

                    <CRUModal
                        isOpen={resultsModalState.isOpen}
                        onClose={handleResultsModalClose}
                        title={`Email Results - ${resultsModalState.appointmentDate}`}
                        data={resultsModalState.data}
                        fields={resultsFields}
                        mode={resultsModalState.mode}
                        buttonLabels={{
                            genericCancelLabel: 'Close'
                        }}
                    />
                </>
            )}

            <div>
                <Header as="h2">
                    Step #1: <span style={{fontSize: '1.7rem', fontWeight: 'normal'}}>
                        Please click View Timeslots to show available timeslots for that day.
                    </span>
                </Header>
                {isAdmin && (
                    <TableLegend
                        items={legendItems}
                        hasPermission={() => true}  // Adjust permission check as needed
                    />
                )}
            </div>
            {loading ? (
                <p>Loading</p>
            ) : appointmentDates.data?.length > 0 ? (
                <div className="table-wrapper" style={{marginTop: '30px', maxWidth: '700px'}}>
                    <DynamicTable
                        initialData={appointmentDates}
                        options={options}
                        style={{width: '300px'}}
                        resetStateOnMount={true}
                    />
                </div>
            ) : (
                <p>{noAppointmentDatesMessage}</p>
            )}
        </div>
    );
}

export default GeneralDashboard;